import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { apiFetchVendor } from "@/utilities/VendorUtilities";
import { useAppState } from "@/context/app-state";
import Alert from "../shared/Alert";
import Footer from "../shared/Footer";
import TextInput from "../shared/TextInput";
import ReplicatedInfo from "./ReplicatedInfo";
import { Button, ButtonLink } from "../shared/Button";

type VerificationState = "idle" | "verifying" | "success" | "error";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const codeInputRef = useRef<HTMLInputElement>(null);
  const { isAuthenticated } = useAppState();

  const emailFromUrl = searchParams.get("email") || "";
  const codeFromUrl = searchParams.get("code") || "";

  const [email, setEmail] = useState(emailFromUrl);
  const [code, setCode] = useState(codeFromUrl);
  const [state, setState] = useState<VerificationState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Auto-verify on mount if user is authenticated and both email and code are in URL
  useEffect(() => {
    if (isAuthenticated && emailFromUrl && codeFromUrl) {
      handleVerify(emailFromUrl, codeFromUrl);
    } else if (codeInputRef.current && !emailFromUrl && isAuthenticated) {
      // Focus input if no email in URL and user is authenticated
      codeInputRef.current.focus();
    }
  }, [isAuthenticated]);

  const handleVerify = async (emailToVerify: string, codeToVerify: string) => {
    if (!emailToVerify || !codeToVerify) {
      setErrorMessage("Email address and verification code are required");
      setState("error");
      return;
    }

    setState("verifying");
    setErrorMessage(null);

    try {
      const response = await apiFetchVendor("/notification_email/verify", {
        method: "POST",
        body: JSON.stringify({
          emailAddress: emailToVerify,
          verificationCode: codeToVerify.toUpperCase()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg = errorData.error || `Failed to verify email: ${response.status}`;
        setErrorMessage(errorMsg);
        setState("error");
        return;
      }

      // Success
      setState("success");
    } catch (err: any) {
      setErrorMessage(err.message || "Failed to verify email. Please try again.");
      setState("error");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setErrorMessage("Verification code is required");
      setState("error");
      return;
    }
    handleVerify(email, code.trim());
  };

  const handleCodeChange = (inputValue: string) => {
    // Only allow alphanumeric characters and limit to 6
    const value = inputValue.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (value.length <= 6) {
      setCode(value);
      if (errorMessage) {
        setErrorMessage(null);
        setState("idle");
      }
    }
  };

  return (
    <div className="min-h-full w-full overflow-auto flex flex-col">
      <div className="flex flex-col-reverse shrink-0 min-[800px]:flex-row flex-auto w-full">
        <ReplicatedInfo needsActivation={true} />
        <div className="bg-page-bg lg:ml-0 lg:text-left flex-1 min-h-0 flex flex-col items-center justify-center pb-3 text-left auth-pages">
          <h1 className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0 text-indigo-500 !mt-0 lg:!mt-[70px] text-2xl lg:text-3xl !mb-6">
            Verify Notification Email
          </h1>
          <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
            {state === "success" && (
              <div className="space-y-8">
                <p className="mt-2 text-gray-700 text-base">
                  Your email address has been verified. You can now use it for notification subscriptions.
                </p>
                <div className="FormButton-wrapper">
                  <ButtonLink
                    type="button"
                    kind="primary-purple"
                    size="large"
                    to="/notifications"
                  >
                    Go to Notifications
                  </ButtonLink>
                </div>
              </div>
            )}
            {state !== "success" && (
              <>
                {!isAuthenticated && (
                  <div className="space-y-6">
                    <p className="font-['Open_Sans',sans-serif] text-gray-410 text-base leading-[29px] font-normal mt-3">
                      Please log in to verify your email address for notifications.
                    </p>
                    <Alert variant="warning">
                      You must log in to verify your email address for notifications.
                    </Alert>
                    <div className="FormButton-wrapper">
                      <ButtonLink
                        type="button"
                        kind="primary-purple"
                        size="large"
                        to="/login"
                      >
                        Log in
                      </ButtonLink>
                    </div>
                  </div>
                )}
                {isAuthenticated && (
                  <>
                    <p className="font-['Open_Sans',sans-serif] text-gray-410 text-base leading-[29px] font-normal mt-3">
                      {emailFromUrl
                        ? `Enter the verification code sent to ${emailFromUrl}`
                        : "Enter your email address and the verification code sent to you"}
                    </p>
                    <form onSubmit={handleSubmit}>
                      <div className="FormSection-wrapper">
                        {state === "error" && errorMessage && (
                          <Alert variant="error" className="mb-1.5">
                            {errorMessage}
                          </Alert>
                        )}
                        {state === "verifying" && (
                          <Alert variant="info" className="mb-4">
                            Verifying email...
                          </Alert>
                        )}
                        {!emailFromUrl && (
                          <div className="mb-4">
                            <label htmlFor="email" className="hidden">
                              Email address
                            </label>
                            <TextInput
                              name="email"
                              type="email"
                              placeholder="Email address"
                              value={email}
                              onChangeValue={value => {
                                setEmail(value);
                                if (errorMessage) {
                                  setErrorMessage(null);
                                  setState("idle");
                                }
                              }}
                              required
                              disabled={state === "verifying"}
                            />
                          </div>
                        )}
                        <div>
                          <label htmlFor="verificationCode" className="hidden">
                            Verification code
                          </label>
                          <TextInput
                            name="verificationCode"
                            className="text-center text-2xl tracking-widest uppercase"
                            type="text"
                            ref={codeInputRef}
                            placeholder="XXXXXX"
                            value={code}
                            onChangeValue={handleCodeChange}
                            maxLength={6}
                            required
                            disabled={state === "verifying" || (!!codeFromUrl && state !== "error")}
                            autoFocus={!codeFromUrl}
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            Enter the 6-character code from your email
                          </p>
                        </div>
                      </div>
                      <div className="FormButton-wrapper flex flex-wrap mb-8 gap-4">
                        <Button
                          type="submit"
                          kind="primary-purple"
                          size="large"
                          disabled={state === "verifying" || !code || !email}
                        >
                          {state === "verifying" ? "Verifying..." : "Verify email"}
                        </Button>
                      </div>
                    </form>
                  </>
                )}
              </>
            )}
          </div>
          <div className="absolute bottom-[10px] right-[10px] !text-gray-200 max-lg:mx-auto max-lg:pt-[10px] max-lg:static max-lg:w-full max-lg:text-center max-lg:min-h-[100px]">
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
